import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DateTime } from "luxon";
import { collection, collectionGroup, onSnapshot, doc, query, setDoc, where } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useFirm } from "../contexts/FirmContext";

// ==========================================
// Types & Interfaces mirroring Vision §0.2
// ==========================================

export type RoleType = "OWNER" | "ADMIN" | "MEMBER" | "PARALEGAL" | "ACCOUNTANT" | "READ_ONLY";
export type LanguageType = "en" | "ar";

// Sprint 11.3: Production hardening. In production the app must never silently fall
// back to seeded mock matters when Firestore is unreachable, empty, or firmId is
// missing — doing so masks data-bootstrap bugs and shows fake matters to real users.
// Mock seeds remain available for local development by opting in explicitly via
// VITE_USE_MOCK_DATA=1 in .env.local.
const env = (import.meta as unknown as {
  env?: {
    PROD?: boolean;
    VITE_USE_MOCK_DATA?: string;
    VITE_ALLOW_PRODUCTION_MOCK_DATA?: string;
    VITE_FIREBASE_API_KEY?: string;
  };
}).env;

export const USE_MOCK_DATA: boolean =
  env?.VITE_USE_MOCK_DATA === "1" &&
  (env?.PROD !== true || env?.VITE_ALLOW_PRODUCTION_MOCK_DATA === "1");

const USE_LOCAL_PREVIEW_DATA = env?.PROD !== true && !env?.VITE_FIREBASE_API_KEY;
const LOCAL_PREVIEW_MATTERS_KEY = "iamv:local-preview:matters";
const LOCAL_PREVIEW_MATTERS_EVENT = "iamv:local-preview-matters-changed";

// L1.4/L1.5: language persistence + document direction governance.
// `lang` must survive reloads and govern the document's `dir`/`lang` attributes
// app-wide (not just per-container), otherwise scrollbars, fixed overlays, and
// the public dual-render CSS desynchronize.
const LANG_STORAGE_KEY = "iamv:lang";

function readStoredLang(): LanguageType {
  if (typeof window === "undefined") return "en";
  try {
    const stored = window.localStorage.getItem(LANG_STORAGE_KEY);
    if (stored === "ar" || stored === "en") return stored;
  } catch {
    /* localStorage unavailable (private mode / SSR) — fall back to default */
  }
  return "en";
}

function applyDocumentLanguage(lang: LanguageType): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.setAttribute("lang", lang);
  root.setAttribute("dir", lang === "ar" ? "rtl" : "ltr");
}

function mockOrEmpty<T>(seed: T[]): T[] {
  return USE_MOCK_DATA ? seed : [];
}

function normalizeClientKycStatus(value: unknown): Client["kycStatus"] {
  const normalized = typeof value === "string" ? value.trim().toLowerCase() : "";
  if (normalized === "expired") return "expired";
  if (normalized === "warning30d" || normalized === "warning_30d") return "warning30d";
  return "valid";
}

// Sprint 12.2: object-shaped equivalent of mockOrEmpty for non-array seeds
// (e.g. the Drafting Studio activeDraft, which is a single document). Returns
// the seeded fixture in mock mode and a blank-but-shape-compatible fallback
// in production so the editor still mounts without rendering fake content.
function mockOrFallback<T>(seed: T, fallback: T): T {
  return USE_MOCK_DATA ? seed : fallback;
}

function normalizeMatterRecord(docId: string, data: Record<string, unknown>, fallbackSpecies: Matter["matterSpecies"]): Matter {
  const matterId = typeof data.matterId === "string" && data.matterId ? data.matterId : docId;
  const matterSpecies = data.matterSpecies === "CONTRACT" || fallbackSpecies === "CONTRACT" ? "CONTRACT" : "CASE";

  return {
    matterId,
    id: docId,
    title: typeof data.title === "string" ? data.title : "",
    matterSpecies,
    status: typeof data.status === "string" ? data.status : undefined,
    contractType: typeof data.contractType === "string" ? data.contractType as Matter["contractType"] : undefined,
    lifecycle: typeof data.lifecycle === "string" && data.lifecycle ? data.lifecycle : "INTAKE",
    clientId: typeof data.clientId === "string" ? data.clientId : "",
    clientName: typeof data.clientName === "string" ? data.clientName : "",
    leadLawyer: typeof data.leadLawyer === "string"
      ? data.leadLawyer
      : typeof data.lead === "string"
        ? data.lead
        : typeof data.ownerUserId === "string"
          ? data.ownerUserId
          : typeof data.responsibleOwnerUid === "string"
            ? data.responsibleOwnerUid
            : "Unassigned",
    assignedTeam: Array.isArray(data.assignedTeam) ? data.assignedTeam as Matter["assignedTeam"] : [],
    jurisdiction: typeof data.jurisdiction === "string" ? data.jurisdiction : "",
    solClockDays: typeof data.solClockDays === "number" ? data.solClockDays : undefined,
    damagesClaimed: typeof data.damagesClaimed === "string" ? data.damagesClaimed : undefined,
    valueAED: typeof data.valueAED === "string" ? data.valueAED : undefined,
    termMonths: typeof data.termMonths === "number" ? data.termMonths : undefined,
    autoRenew: typeof data.autoRenew === "boolean" ? data.autoRenew : undefined,
    counterpartyName: typeof data.counterpartyName === "string" ? data.counterpartyName : undefined,
    counterpartyType: typeof data.counterpartyType === "string" ? data.counterpartyType : undefined,
    nextEvent: typeof data.nextEvent === "string" ? data.nextEvent : "",
    nextEventDate: typeof data.nextEventDate === "string" ? data.nextEventDate : "",
    riskScore: data.riskScore === "High" || data.riskScore === "Low" ? data.riskScore : "Medium",
    wipValue: typeof data.wipValue === "number" ? data.wipValue : 0,
    arValue: typeof data.arValue === "number" ? data.arValue : 0,
    lastActivity: typeof data.lastActivity === "string" ? data.lastActivity : "",
    flags: Array.isArray(data.flags) ? data.flags as string[] : [],
    evidence: Array.isArray(data.evidence) ? data.evidence as EvidenceItem[] : [],
    witnesses: Array.isArray(data.witnesses) ? data.witnesses as Witness[] : [],
    obligations: Array.isArray(data.obligations) ? data.obligations as Obligation[] : [],
    redlineRounds: Array.isArray(data.redlineRounds) ? data.redlineRounds as RedlineRound[] : [],
    riskRegister: Array.isArray(data.riskRegister) ? data.riskRegister as RiskItem[] : [],
    underAmendment: typeof data.underAmendment === "boolean" ? data.underAmendment : undefined,
  };
}

function readLocalPreviewMatters(): Matter[] {
  if (typeof window === "undefined") return [];

  try {
    const raw = window.localStorage.getItem(LOCAL_PREVIEW_MATTERS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed.filter((item): item is Matter => Boolean(item && typeof item === "object" && typeof item.matterId === "string" && typeof item.title === "string"))
      : [];
  } catch {
    return [];
  }
}

function mergeLocalPreviewMatters(liveMatters: Matter[]): Matter[] {
  const merged = new Map<string, Matter>();

  for (const matter of readLocalPreviewMatters()) {
    merged.set(matter.matterId, matter);
  }

  for (const matter of liveMatters) {
    merged.set(matter.matterId, matter);
  }

  return Array.from(merged.values());
}

export interface Party {
  partyId: string;
  name: string;
  type: "INDIVIDUAL" | "ENTITY" | "GOVERNMENT" | "COURT" | "LAW_FIRM";
  jurisdiction: string;
  representingCounsel?: string;
  relationship: "PLAINTIFF" | "DEFENDANT" | "COUNTER_PARTY" | "OPPOSING_PARTY";
}

export interface EvidenceItem {
  id: string;
  name: string;
  type: "Witness Statement" | "Medical Record" | "Invoice" | "Audio" | "Video" | "Email" | "Court Order" | "Expert Report" | "Construction Spec";
  uploadedAt: string;
  status: "NEW" | "ANALYZING" | "VALIDATED" | "NEEDS_REVIEW" | "INTEGRITY_COMPROMISED" | "PRIVILEGED" | "REDACTED_READY";
  size: string;
  entities: string[];
  datesMentioned: string[];
  admissibilityFlags: string[];
  excerpt: string;
  validatedBy?: string;
  hasAiInsight: boolean;
}

export interface Witness {
  witnessId: string;
  name: string;
  role: "FACT" | "EXPERT" | "CHARACTER";
  side: "PLAINTIFF" | "DEFENDANT" | "NEUTRAL";
  credibility: "HIGH" | "MEDIUM" | "LOW" | "UNASSESSED";
  testimonySummary: string;
  statementDocId?: string;
  contradictionCount: number;
  crossExamQuestions: string[];
}

export interface Obligation {
  id: string;
  title: string;
  dueAt: string;
  status: "PENDING" | "COMPLETED" | "OVERDUE";
  assignedTo: string;
  triggerType: "Milestone" | "Notice Window" | "Payment Event";
}

export interface RedlineRound {
  roundId: number;
  date: string;
  sender: string;
  status: "RESOLVED" | "UNRESOLVED" | "PENDING_REVIEW";
  summary: string;
  clauseChanges: {
    section: string;
    originalText: string;
    proposedText: string;
    status: "ACCEPTED" | "REJECTED" | "PENDING";
    aiConfidence: string;
  }[];
}

export interface RiskItem {
  id: string;
  clause: string;
  severity: "CRITICAL" | "HIGH" | "MEDIUM" | "LOW";
  mitigation: string;
  description: string;
}

export interface Matter {
  matterId: string; // CASE-2026-0042, CON-2026-0117, etc
  id?: string; // Alias for matterId used by some route components
  title: string;
  matterSpecies: "CASE" | "CONTRACT";
  status?: string; // Used by analytics/work-queue route pages
  contractType?: "TRANSACTIONAL" | "ADVISORY_OPINION" | "COMPLIANCE_REVIEW";
  lifecycle: string; // INTAKE -> ACTIVE -> IN_HEARING -> etc
  clientId: string;
  clientName: string;
  leadLawyer: string;
  assignedTeam: { name: string; avatar: string; email: string }[];
  jurisdiction: string;
  solClockDays?: number; // Statute of limitations remaining
  damagesClaimed?: string;
  valueAED?: string;
  termMonths?: number;
  autoRenew?: boolean;
  counterpartyName?: string;
  counterpartyType?: string;
  nextEvent: string;
  nextEventDate: string;
  riskScore: "High" | "Medium" | "Low";
  wipValue: number;
  arValue: number;
  lastActivity: string;
  flags: string[];
  evidence: EvidenceItem[];
  witnesses: Witness[];
  obligations: Obligation[];
  redlineRounds: RedlineRound[];
  riskRegister: RiskItem[];
  underAmendment?: boolean;
}

export interface Client {
  recordId?: string;
  clientId: string;
  name: string;
  status: "PROSPECT" | "ONBOARDING" | "ACTIVE" | "RESTRICTED" | "ARCHIVED";
  type: "Individual" | "Entity";
  industry: string;
  languages: string[];
  engagedAt: string;
  kycExpiry: string;
  kycStatus: "valid" | "warning30d" | "expired";
  amlRisk: "LOW" | "MEDIUM" | "HIGH";
  outstandingAR: number;
  revenueYTD: number;
  contacts: { name: string; email: string; phone: string; portalScope: string }[];
}

export interface WorkQueueItem {
  id: string;
  title: string;
  workflow: "ONBOARD" | "CONTRACT" | "HEARING" | "OBLIG" | "EXPORT";
  owner: string;
  status: "OPEN" | "IN_PROGRESS" | "BLOCKED" | "COMPLETED";
  dueOrAge: string;
  progress: number; // 0 to 100
  reasonBlocked?: string;
  linkedAlertId?: string;
  linkedMatterId?: string;
}

export interface Alert {
  id: string;
  title: string;
  matterId?: string;
  priority: "P0" | "P1" | "P2" | "P3";
  category: "HEARING" | "RENEWAL" | "INVOICE" | "ALERT" | "KYC" | "COMPLIANCE";
  status: "UNRESOLVED" | "RESOLVED";
}

export interface AuditLog {
  id: string;
  timestamp: string;
  actor: string;
  role: string;
  action: string;
  details: string;
  matterId?: string;
}

export interface AppContextType {
  role: RoleType;
  lang: LanguageType;
  currentRoute: string;
  activeMatterId: string;
  activeClientId: string;
  setRole: (r: RoleType) => void;
  setLang: (l: LanguageType) => void;
  setCurrentRoute: (r: string) => void;
  setActiveMatterId: (id: string) => void;
  setActiveClientId: (id: string) => void;
  
  // Data States
  matters: Matter[];
  clients: Client[];
  setClients: React.Dispatch<React.SetStateAction<Client[]>>;
  workQueue: WorkQueueItem[];
  setWorkQueue: React.Dispatch<React.SetStateAction<WorkQueueItem[]>>;
  alerts: Alert[];
  auditLogs: AuditLog[];
  
  // Actions
  rebalanceWorkload: (fromLawyer: string, toLawyer: string, matterId: string) => void;
  runDunningSweep: () => string; // triggers dunning, returns summary
  validateEvidence: (matterId: string, evidenceId: string) => void;
  uploadEvidence: (matterId: string, item: Partial<EvidenceItem>) => void;
  addWitness: (matterId: string, witness: Partial<Witness>) => void;
  addObligation: (matterId: string, obligation: Partial<Obligation>) => void;
  acceptRedlineChange: (matterId: string, roundId: number, section: string, accept: boolean) => void;
  addCustomReminder: (title: string, triggerDays: number, eventName: string, channels: string[]) => void;
  writeLogs: (actor: string, action: string, details: string, matterId?: string) => void;
  
  // Drafting Studio states
  activeDraft: {
    id: string;
    title: string;
    content: string;
    lockHolder?: string;
    lockExpiresAt?: string;
    aiBlocks: number;
    versions: { id: string; name: string; date: string; content: string }[];
  };
  openMatterDraft: (matter: Matter) => void;
  setActiveDraftContent: (content: string) => void;
  claimDraftLock: () => boolean;
  releaseDraftLock: () => void;
  saveDraftVersion: (name: string) => void;
  aiAttributionOpen: boolean;
  setAiAttributionOpen: (open: boolean) => void;
  // Layout states
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  aiCounselOpen: boolean;
  setAiCounselOpen: (open: boolean) => void;
  aiCounselWidth: number; // percentage of desktop screen (20 to 40)
  setAiCounselWidth: (width: number) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

// ==========================================
// Mock Data Generation Providers
// ==========================================

export const AppStateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { firmId, role: firmRole } = useFirm();

  const [role, setRoleState] = useState<RoleType>("OWNER");
  // L1.5: hydrate language from localStorage so a reload no longer reverts to English.
  const [lang, setLangState] = useState<LanguageType>(readStoredLang);

  // L1.5: persist language selection to localStorage immediately on every change.
  const setLang = useCallback((next: LanguageType) => {
    setLangState(next);
    if (typeof window !== "undefined") {
      try {
        window.localStorage.setItem(LANG_STORAGE_KEY, next);
      } catch {
        /* ignore persistence failures (private mode) */
      }
    }
  }, []);

  // L1.4: govern document direction/language app-wide whenever `lang` changes.
  useEffect(() => {
    applyDocumentLanguage(lang);
  }, [lang]);

  // L1.5: hydrate language from the user's Firestore workspace preferences
  // (users/{uid}/preferences/workspace.outputLanguage) once authenticated, so the
  // choice follows the user across devices. localStorage already provides the
  // immediate, offline-safe value above; this reconciles the persisted server value.
  useEffect(() => {
    if (!user?.uid) return;
    const unsub = onSnapshot(
      doc(db, "users", user.uid, "preferences", "workspace"),
      (snap) => {
        const data = snap.data() as { outputLanguage?: unknown } | undefined;
        const serverLang = data?.outputLanguage === "ar" ? "ar" : data?.outputLanguage === "en" ? "en" : null;
        if (serverLang) setLang(serverLang);
      },
      () => {},
    );
    return unsub;
  }, [user?.uid, setLang]);

  const location = useLocation();
  const navigate = useNavigate();
  const currentRoute = location.pathname;
  const setCurrentRoute = (newRoute: string) => {
    navigate(newRoute);
  };

  const [activeMatterId, setActiveMatterId] = useState<string>(USE_MOCK_DATA ? "CASE-2026-0042" : "");
  const [activeClientId, setActiveClientId] = useState<string>(USE_MOCK_DATA ? "RAYZEN" : "");

  useEffect(() => {
    const caseMatch = location.pathname.match(/^\/cases\/([^/]+)/);
    if (caseMatch && caseMatch[1]) {
      setActiveMatterId(caseMatch[1]);
    }
    const contractMatch = location.pathname.match(/^\/contracts\/([^/]+)/);
    if (contractMatch && contractMatch[1]) {
      setActiveMatterId(contractMatch[1]);
    }
    const clientMatch = location.pathname.match(/^\/clients\/([^/]+)/);
    if (clientMatch && clientMatch[1]) {
      setActiveClientId(clientMatch[1]);
    }
  }, [location.pathname]);

  const [aiAttributionOpen, setAiAttributionOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [aiCounselOpen, setAiCounselOpen] = useState(false);
  const [aiCounselWidth, setAiCounselWidth] = useState(24); // default 24% of screen width (around 360px-380px)

  useEffect(() => {
    const media = window.matchMedia("(max-width: 768px)");
    const applyMobileDefault = () => {
      if (media.matches) setAiCounselOpen(false);
    };
    applyMobileDefault();
    media.addEventListener("change", applyMobileDefault);
    return () => media.removeEventListener("change", applyMobileDefault);
  }, []);

  useEffect(() => {
    if (!firmRole) return;
    setRoleState(firmRole as RoleType);
  }, [firmRole]);

  // Sync route with Landing Page expectations for "MEMBER" (Senior Associate)
  const setRole = (newRole: RoleType) => {
    if (!USE_MOCK_DATA) return;
    setRoleState(newRole);
    if (newRole === "MEMBER") {
      navigate("/my-day");
    } else {
      navigate("/cockpit");
    }
  };

  // Mock Clients list §1.4, §4.3, §6.8
  // Sprint 12.2: seed extracted to MOCK_CLIENTS_SEED and gated behind
  // VITE_USE_MOCK_DATA so production renders an empty clients list instead
  // of fake Rayzen/Acme/Nova fixtures. A live Firestore listener will be
  // wired in Sprint 12.3.
  const MOCK_CLIENTS_SEED: Client[] = [
    {
      clientId: "RAYZEN",
      name: "Rayzen FZE",
      status: "ACTIVE",
      type: "Entity",
      industry: "Technology",
      languages: ["English", "Arabic"],
      engagedAt: "2026-03-12T00:00:00Z",
      kycExpiry: "2027-01-15T00:00:00Z",
      kycStatus: "valid",
      amlRisk: "LOW",
      outstandingAR: 22000,
      revenueYTD: 184000,
      contacts: [
        { name: "John Rayzen", email: "john@rayzen.ae", phone: "+971 50 123 4567", portalScope: "Full Access" },
        { name: "Amna Al-Mansoori", email: "amna@rayzen.ae", phone: "+971 54 987 6543", portalScope: "Signatory-Only" }
      ]
    },
    {
      clientId: "ACME",
      name: "Acme Holding",
      status: "ACTIVE",
      type: "Entity",
      industry: "Real Estate & Logistics",
      languages: ["English"],
      engagedAt: "2026-01-10T00:00:00Z",
      kycExpiry: DateTime.now().plus({ days: 28 }).toISO() || "2026-06-25T00:00:00Z",
      kycStatus: "warning30d",
      amlRisk: "MEDIUM",
      outstandingAR: 312000,
      revenueYTD: 480000,
      contacts: [
        { name: "Rashid Al-Habtoor", email: "rashid@acmeholdings.ae", phone: "+971 52 444 8888", portalScope: "Full Access" },
        { name: "Tariq Ali", email: "tariq@acmeholdings.ae", phone: "+971 50 555 1111", portalScope: "Billing-Only" }
      ]
    },
    {
      clientId: "NOVA",
      name: "Nova Industries",
      status: "RESTRICTED",
      type: "Entity",
      industry: "Trading",
      languages: ["English", "Arabic"],
      engagedAt: "2025-11-05T00:00:00Z",
      kycExpiry: DateTime.now().minus({ days: 5 }).toISO() || "2026-05-20T00:00:00Z",
      kycStatus: "expired",
      amlRisk: "HIGH",
      outstandingAR: 45000,
      revenueYTD: 120000,
      contacts: [
        { name: "Firas Salem", email: "firas@nova.ae", phone: "+971 50 999 0000", portalScope: "Full Access" }
      ]
    }
  ];
  const [clients, setClients] = useState<Client[]>(mockOrEmpty(MOCK_CLIENTS_SEED));

  // Mock Evidence list §5.3, §3.2.2, §3.8
  const defaultEvidenceCase42: EvidenceItem[] = [
    {
      id: "ev-01",
      name: "Statement of Fact-Witness Khan.pdf",
      type: "Witness Statement",
      uploadedAt: "2026-05-18T10:15:00Z",
      status: "VALIDATED",
      size: "820 KB",
      entities: ["Yousuf Khan", "Rayzen FZE", "DIFC Courts"],
      datesMentioned: ["12 March 2026", "13 March 2026"],
      admissibilityFlags: ["Hearsay Risk on claimant's oral agreement claim"],
      excerpt: "I was present when the oral contract extension was spoken on 12 March. The director agreed to a fee increase of 20% in front of witnesses...",
      hasAiInsight: true,
      validatedBy: "Sabique Ahmed (SA)"
    },
    {
      id: "ev-02",
      name: "Construction Delay Timeline Log.xlsx",
      type: "Construction Spec",
      uploadedAt: "2026-05-21T14:30:00Z",
      status: "NEW",
      size: "4.2 MB",
      entities: ["Rayzen Project Site", "Gulf Constructors Inc", "AED 5M Contingency"],
      datesMentioned: ["15 April 2026", "02 May 2026"],
      admissibilityFlags: ["Unsigned log file - authentication required"],
      excerpt: "Log entries indicate major machinery failures on 18 April, creating an active disruption cascade along the critical path for foundation delivery...",
      hasAiInsight: true
    },
    {
      id: "ev-03",
      name: "Disputed Payment Invoice INV-447.pdf",
      type: "Invoice",
      uploadedAt: "2026-05-23T11:00:00Z",
      status: "INTEGRITY_COMPROMISED",
      size: "1.4 MB",
      entities: ["Rayzen FZE", "Acme Holdings", "AED 45,000"],
      datesMentioned: ["01 May 2026"],
      admissibilityFlags: ["Sealed digital signature mismatch", "Incorrect TRN recorded"],
      excerpt: "Invoice total recorded as AED 45,000 instead of agreed contract base AED 450,000. Severe drafting discrepancy in tax code reference...",
      hasAiInsight: true
    }
  ];

  // Mock Witnesses §3.8
  const defaultWitnessesCase42: Witness[] = [
    {
      witnessId: "wit-01",
      name: "Yousuf Khan",
      role: "FACT",
      side: "PLAINTIFF",
      credibility: "HIGH",
      testimonySummary: "Corroborates the oral agreement on the extension fee of 20% during the meeting of 12 March 2026. Consistent with contemporaneous chat logs.",
      statementDocId: "ev-01",
      contradictionCount: 0,
      crossExamQuestions: [
        "Confirm who spoke first regarding the 20% increase.",
        "Clarify if any written correspondence followed this verbal agreement.",
        "Demonstrate if the witness had any financial interest in Rayzen FZE outcomes."
      ]
    },
    {
      witnessId: "wit-02",
      name: "Dr. Hassan Al-Mansoori",
      role: "EXPERT",
      side: "NEUTRAL",
      credibility: "MEDIUM",
      testimonySummary: "Submitted expert forensic report. Confirmed that some delay logs in excel files are missing critical file hashes from early April storage operations.",
      contradictionCount: 1,
      crossExamQuestions: [
        "Detail your scientific verification method on Excel file hashes.",
        "Address opposing expert findings stating that logs could not be altered post-facto.",
        "Support your view on whether delays were objectively caused by Force Majeure weather events."
      ]
    }
  ];

  // Mock Obligations Register §4.1, §4.3
  const defaultObligationsContract117: Obligation[] = [
    { id: "ob-01", title: "AED 120,000 Retainer Payment Due", dueAt: "2026-06-12", status: "PENDING", assignedTo: "Mary K. (MK)", triggerType: "Payment Event" },
    { id: "ob-02", title: "Submit 30-Day Written Expiry Notice", dueAt: "2026-07-28", status: "OVERDUE", assignedTo: "Sabique Ahmed (SA)", triggerType: "Notice Window" },
    { id: "ob-03", title: "Deliver Tech Audit Statement of Works", dueAt: "2026-08-15", status: "PENDING", assignedTo: "Amna Al-Mansoori", triggerType: "Milestone" }
  ];

  // Mock Negotiation Rounds §4.3, §6.4
  const defaultRedlineContract117: RedlineRound[] = [
    {
      roundId: 3,
      date: "2026-05-23T14:02:00Z",
      sender: "Bishop & Co (Counter-party Counsel)",
      status: "UNRESOLVED",
      summary: "Bishop & Co rejected our liability cap proposal and amended term convenience definitions.",
      clauseChanges: [
        {
          section: "§3 Limitation of Liability",
          originalText: "The Contractor's total aggregate liability under this agreement is strictly capped at 2× the annual contract fee (AED 960,000) except for willful misconduct.",
          proposedText: "The Contractor's liability under this agreement remains completely unlimited for all direct damages, breaches of data protection laws, or indemnities.",
          status: "PENDING",
          aiConfidence: "94% deviation from firm playbook standard. Recommended Reject."
        },
        {
          section: "§5 Intellectual Property Rights",
          originalText: "All Background IP remains vested in the granting party. Developed IP shall transfer to Client exclusively upon final unappealable settlement of invoices.",
          proposedText: "Developed IP shall transfer to Client immediately upon creation, irrespective of whether payments are pending, subject to retroactive licensing.",
          status: "ACCEPTED",
          aiConfidence: "Balanced position. Accept with minor wording safeguards."
        }
      ]
    }
  ];

  // Mock Contract Risk Register §4.5.5
  const defaultRisksContract117: RiskItem[] = [
    {
      id: "risk-01",
      clause: "Section 3: Unlimited Liability Draft",
      severity: "CRITICAL",
      mitigation: "Negotiate a hard liability cap of 2× the annual billable fee (AED 960,000) with solid carving out options.",
      description: "Unlimited liability expose Rayzen and Acme to sweeping claims in case of systems failures under UAE Civil Code."
    },
    {
      id: "risk-02",
      clause: "Section 12.2: Auto-renewal with 30-day notice limit",
      severity: "HIGH",
      mitigation: "Establish a push calendar reminder at T-60 and notify client via Secure Portal 45 days prior.",
      description: "Auto-renewal locks client for another 12-month term unless notice is delivered to physical DIFC office."
    }
  ];

  // Seed Matters list §0.2, §1.4, §3 & §4
  const MOCK_MATTERS_SEED: Matter[] = [
    {
      matterId: "CASE-2026-0042",
      title: "Smith v. Gulf Construction Corp",
      matterSpecies: "CASE",
      lifecycle: "IN_HEARING",
      clientId: "RAYZEN",
      clientName: "Rayzen FZE",
      leadLawyer: "Sabique Ahmed (SA)",
      assignedTeam: [
        { name: "Sabique Ahmed", avatar: "SA", email: "sabiqahmed@gmail.com" },
        { name: "Mary K.", avatar: "MK", email: "mary@iamvault.com" },
        { name: "Adil Sharaf", avatar: "AS", email: "adil@iamvault.com" }
      ],
      jurisdiction: "DIFC Courts",
      solClockDays: 45,
      damagesClaimed: "AED 3,600,000",
      nextEvent: "Hearing - Pleadings Review",
      nextEventDate: "2026-05-28T09:30:00Z",
      riskScore: "High",
      wipValue: 152000,
      arValue: 22000,
      lastActivity: "2 hours ago by Sabique Ahmed",
      flags: ["Evidence Unvalidated", "No Opposing Party Added", "SOL < 90 Days"],
      evidence: defaultEvidenceCase42,
      witnesses: defaultWitnessesCase42,
      obligations: [],
      redlineRounds: [],
      riskRegister: []
    },
    {
      matterId: "CON-2026-0117",
      title: "Acme Holding Master Services Agreement",
      matterSpecies: "CONTRACT",
      contractType: "TRANSACTIONAL",
      lifecycle: "NEGOTIATING",
      clientId: "ACME",
      clientName: "Acme Holding",
      leadLawyer: "Mary K. (MK)",
      assignedTeam: [
        { name: "Mary K.", avatar: "MK", email: "mary@iamvault.com" },
        { name: "Sabique Ahmed", avatar: "SA", email: "sabiqahmed@gmail.com" }
      ],
      jurisdiction: "DIFC Onshore",
      valueAED: "AED 480,000",
      termMonths: 12,
      autoRenew: true,
      nextEvent: "Deal Room - Waiting opposing counsel",
      nextEventDate: "2026-07-28T12:00:00Z",
      riskScore: "Medium",
      wipValue: 48000,
      arValue: 312000,
      lastActivity: "1 day ago by Mary K.",
      flags: ["Unresolved Redlines in Playbook", "Upcoming Renewal Notice"],
      evidence: [],
      witnesses: [],
      obligations: defaultObligationsContract117,
      redlineRounds: defaultRedlineContract117,
      riskRegister: defaultRisksContract117
    },
    {
      matterId: "CON-2026-0203",
      title: "Nova Industries Property Advisory Policy",
      matterSpecies: "CONTRACT",
      contractType: "ADVISORY_OPINION",
      lifecycle: "INTERNAL_REVIEW",
      clientId: "NOVA",
      clientName: "Nova Industries",
      leadLawyer: "Adil Sharaf (AS)",
      assignedTeam: [
        { name: "Adil Sharaf", avatar: "AS", email: "adil@iamvault.com" }
      ],
      jurisdiction: "UAE Mainland Onshore",
      valueAED: "AED 95,000",
      nextEvent: "Partner Sign-off required",
      nextEventDate: "2026-06-02T16:00:00Z",
      riskScore: "High",
      wipValue: 12000,
      arValue: 45000,
      lastActivity: "3 days ago by Adil Sharaf",
      flags: ["KYC Expired - Blocked Action", "AML Risk High"],
      evidence: [],
      witnesses: [],
      obligations: [],
      redlineRounds: [],
      riskRegister: []
    }
  ];

  const [matters, setMatters] = useState<Matter[]>(mockOrEmpty(MOCK_MATTERS_SEED));

  // Firestore Matter listener with graceful fallback
  useEffect(() => {
    if (!user || !firmId) {
      setMatters(mockOrEmpty(MOCK_MATTERS_SEED));
      return;
    }

    if (USE_LOCAL_PREVIEW_DATA) {
      let caseMatters: Matter[] = [];
      let contractMatters: Matter[] = [];

      const syncMatters = () => {
        const merged = mergeLocalPreviewMatters([...caseMatters, ...contractMatters]);
        setMatters(merged.length > 0 ? merged : mockOrEmpty(MOCK_MATTERS_SEED));
      };

      const handleLocalPreviewMatterChange = () => {
        syncMatters();
      };

      if (typeof window !== "undefined") {
        window.addEventListener(LOCAL_PREVIEW_MATTERS_EVENT, handleLocalPreviewMatterChange);
      }

      syncMatters();

      const unsubscribeCases = onSnapshot(
        query(collection(db, "cases"), where("firmId", "==", firmId)),
        (snapshot) => {
          caseMatters = snapshot.docs.map((record) => normalizeMatterRecord(record.id, record.data() as Record<string, unknown>, "CASE"));
          syncMatters();
        },
        (error) => {
          console.error("Firestore cases snapshot error:", error);
          caseMatters = [];
          syncMatters();
        },
      );

      const unsubscribeContracts = onSnapshot(
        query(collection(db, "contracts"), where("firmId", "==", firmId)),
        (snapshot) => {
          contractMatters = snapshot.docs.map((record) => normalizeMatterRecord(record.id, record.data() as Record<string, unknown>, "CONTRACT"));
          syncMatters();
        },
        (error) => {
          console.error("Firestore contracts snapshot error:", error);
          contractMatters = [];
          syncMatters();
        },
      );

      return () => {
        if (typeof window !== "undefined") {
          window.removeEventListener(LOCAL_PREVIEW_MATTERS_EVENT, handleLocalPreviewMatterChange);
        }
        unsubscribeCases();
        unsubscribeContracts();
      };
    }

    const mattersRef = query(collectionGroup(db, "matters"), where("firmId", "==", firmId));
    const unsubscribe = onSnapshot(
      mattersRef,
      (snapshot) => {
        if (snapshot.empty) {
          setMatters(mockOrEmpty(MOCK_MATTERS_SEED));
        } else {
          const list = snapshot.docs.map((record) => normalizeMatterRecord(record.id, record.data() as Record<string, unknown>, "CASE"));
          setMatters(list);
        }
      },
      (error) => {
        console.error("Firestore matters snapshot error:", error);
        // Sprint 11.3: only fall back to seeded matters in mock mode; in prod surface empty
        // so the UI can render its proper empty-state instead of fake data.
        setMatters(mockOrEmpty(MOCK_MATTERS_SEED));
      }
    );

    return () => unsubscribe();
  }, [user, firmId]);

  // ============================================================
  // Sprint 12.3 — Firestore listeners for the remaining slices
  // ------------------------------------------------------------
  // Each subscribes to a firm-scoped subcollection (firms/{firmId}/...) and
  // mirrors the matters listener contract: empty snapshot → mockOrEmpty(seed)
  // (so mock mode still shows fixtures, prod shows the real empty-state); on
  // error → same fallback + console diagnostic. Listeners are no-ops until
  // both `user` and `firmId` are present, matching invite-only gating. Live
  // mutators (setDoc/updateDoc/deleteDoc) will be threaded through the
  // existing setter wrappers in Sprint 12.4 once route extraction lands.
  // ============================================================

  // Mock Work Queue §1.9, §16.1
  // Sprint 12.2: seed extracted to MOCK_WORK_QUEUE_SEED and gated behind
  // VITE_USE_MOCK_DATA so production renders an empty queue instead of fake
  // items. Live Firestore listener wired in Sprint 12.3.
  const MOCK_WORK_QUEUE_SEED: WorkQueueItem[] = [
    {
      id: "q-01",
      title: "Approve Round 3 Redlines — Bishop & Co MSA",
      workflow: "CONTRACT",
      owner: "Mary K. (MK)",
      status: "OPEN",
      dueOrAge: "today",
      progress: 60,
      linkedAlertId: "al-02",
      linkedMatterId: "CON-2026-0117"
    },
    {
      id: "q-02",
      title: "Generate Case Hearing Brief — Smith v. Gulf",
      workflow: "HEARING",
      owner: "Sabique Ahmed (SA)",
      status: "OPEN",
      dueOrAge: "in 3 days",
      progress: 10,
      linkedAlertId: "al-01",
      linkedMatterId: "CASE-2026-0042"
    },
    {
      id: "q-03",
      title: "Source Missing Corporate Trade Licence — Nova",
      workflow: "ONBOARD",
      owner: "Mary K. (MK)",
      status: "BLOCKED",
      dueOrAge: "5d age",
      progress: 40,
      reasonBlocked: "Awaiting Client portal upload of renewed trade permit",
      linkedAlertId: "al-04",
      linkedMatterId: "CON-2026-0203"
    },
    {
      id: "q-04",
      title: "Resolve Risk: §3 Liability Cap — CON-117",
      workflow: "CONTRACT",
      owner: "Mary K. (MK)",
      status: "OPEN",
      dueOrAge: "2 days",
      progress: 30,
      linkedMatterId: "CON-2026-0117"
    }
  ];
  const [workQueue, setWorkQueue] = useState<WorkQueueItem[]>(mockOrEmpty(MOCK_WORK_QUEUE_SEED));

  // Mock Alerts §6.2, §9.1
  // Sprint 12.2: seed extracted to MOCK_ALERTS_SEED and gated behind
  // VITE_USE_MOCK_DATA. Live Firestore listener wired in Sprint 12.3.
  const MOCK_ALERTS_SEED: Alert[] = [
    { id: "al-01", title: "Upcoming DIFC Court Hearing in 3 Days (0042)", matterId: "CASE-2026-0042", priority: "P0", category: "HEARING", status: "UNRESOLVED" },
    { id: "al-02", title: "Round 3 Redline Received - Critical Liability Deviation (0117)", matterId: "CON-2026-0117", priority: "P1", category: "RENEWAL", status: "UNRESOLVED" },
    { id: "al-03", title: "Outstanding Invoice INV-292 Overdue 14 Days (Acme)", matterId: "CON-2026-0117", priority: "P1", category: "INVOICE", status: "UNRESOLVED" },
    { id: "al-04", title: "KYC Documents Expired for Nova Industries Onshore Scope", matterId: "CON-2026-0203", priority: "P2", category: "KYC", status: "UNRESOLVED" }
  ];
  const [alerts, setAlerts] = useState<Alert[]>(mockOrEmpty(MOCK_ALERTS_SEED));

  // Mock Audit Logs §10.2
  // Sprint 12.2: seed extracted to MOCK_AUDIT_LOGS_SEED and gated behind
  // VITE_USE_MOCK_DATA. Live Firestore listener wired in Sprint 12.3.
  const MOCK_AUDIT_LOGS_SEED: AuditLog[] = [
    { id: "log-01", timestamp: "2026-05-25T09:12:00Z", actor: "Sabique Ahmed", role: "OWNER", action: "MATTER_GATE_CG2", details: "Acme Holding activated with engagement letter finalized.", matterId: "CON-2026-0117" },
    { id: "log-02", timestamp: "2026-05-25T08:30:00Z", actor: "Mary K.", role: "ADMIN", action: "EVIDENCE_UPLOADED", details: "Uploaded construction delay log. Enqueued for structural OCR parsing.", matterId: "CASE-2026-0042" },
    { id: "log-03", timestamp: "2026-05-24T16:02:00Z", actor: "System Agent", role: "AGENT", action: "AML_RISK_SCREEN", details: "Auto screening of Rayzen FZE completed. Risk profile set to LOW.", matterId: "CASE-2026-0042" }
  ];
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>(mockOrEmpty(MOCK_AUDIT_LOGS_SEED));

  // Unified Audit Logger
  const writeLogs = (actorName: string, actionName: string, msg: string, mId?: string) => {
    const newLog: AuditLog = {
      id: "log-" + Math.floor(Math.random() * 100000),
      timestamp: DateTime.now().setZone("Asia/Dubai").toISO() || new Date().toISOString(),
      actor: actorName,
      role: role,
      action: actionName,
      details: msg,
      matterId: mId
    };
    setAuditLogs(prev => [newLog, ...prev]);
  };

  // Sovereign Drafting Studio states §5, §5.6, §5.10
  // Sprint 12.2: seed extracted to MOCK_ACTIVE_DRAFT_SEED and gated behind
  // VITE_USE_MOCK_DATA. In production the editor mounts with a blank draft
  // shell instead of the Bishop & Co MSA fixture; a real draft loader will
  // be wired in Sprint 12.3 against firms/{firmId}/drafts/{draftId}.
  interface ActiveDraftState {
    id: string;
    title: string;
    content: string;
    aiBlocks: number;
    lockHolder: string | undefined;
    lockExpiresAt: string | undefined;
    versions: { id: string; name: string; date: string; content: string }[];
  }
  const MOCK_ACTIVE_DRAFT_SEED: ActiveDraftState = {
    id: "draft-904",
    title: "Vendor Master Services Agreement (MSA) v3 - Rayzen",
    content: "<h2>MASTER SERVICES AGREEMENT</h2><p>This Agreement is entered into on 26 May 2026, by and between <strong>Rayzen FZE</strong> (Client) and <strong>Bishop & Co</strong> (Contractor).</p><p>WHEREAS, Client desires to retain Contractor to perform advisory operations, and Contractor agrees to render such services consistent with DIFC Onshore guidelines.</p><h3>Section 3: Limitation of Liability</h3><p><span data-ai-generated=\"true\" data-source-ref=\"[clause:liability_cap_v4]\" data-confidence=\"High\" class=\"bg-amber-50 border-l-4 border-amber-400 pl-2 py-1 my-1 block\">The Contractor's total aggregate liability under this agreement is strictly capped at 2× the annual contract fee (AED 960,000) except in cases of proven willful misconduct or gross negligence under the UAE Civil Code.</span></p><p>This AI-attributed clause aligns perfectly with corporate playbooks. You can verify references below before signing off.</p><h3>Section 4: Intellectual Property</h3><p>All background IP will lock strictly under legal rules of ownership.</p>",
    aiBlocks: 1,
    lockHolder: undefined,
    lockExpiresAt: undefined,
    versions: [
      { id: "v1", name: "Initial Template Import", date: "24 May 2026", content: "<h2>MASTER SERVICES AGREEMENT</h2><p>Initial raw boilerplate wording with standard placeholder scopes.</p>" },
      { id: "v2", name: "Internal Review Revision", date: "25 May 2026", content: "<h2>MASTER SERVICES AGREEMENT</h2><p>Amended Section 3 to include the 2× caps liability limit per Playbook rules.</p>" }
    ],
  };
  const EMPTY_ACTIVE_DRAFT_SEED: ActiveDraftState = {
    id: "",
    title: "",
    content: "",
    aiBlocks: 0,
    lockHolder: undefined,
    lockExpiresAt: undefined,
    versions: [],
  };
  const [activeDraft, setActiveDraft] = useState<ActiveDraftState>(
    mockOrFallback(MOCK_ACTIVE_DRAFT_SEED, EMPTY_ACTIVE_DRAFT_SEED),
  );

  const buildMatterDraft = (matter: Matter): ActiveDraftState => {
    const draftKind = matter.matterSpecies === "CONTRACT" ? "Contract Draft" : "Case Draft";
    const coreIssue = matter.matterSpecies === "CONTRACT"
      ? `Counterparty: ${matter.counterpartyName ?? "to be confirmed"}. Lifecycle: ${matter.lifecycle}.`
      : `Forum: ${matter.jurisdiction}. Next event: ${matter.nextEvent}.`;

    return {
      id: `draft-${matter.matterId}`,
      title: `${draftKind} - ${matter.title}`,
      content: `<h2>${matter.title}</h2><p><strong>Client:</strong> ${matter.clientName}</p><p><strong>Matter:</strong> ${matter.matterId}</p><p>${coreIssue}</p><h3>Working Draft</h3><p>Prepare the supervised legal draft for ${matter.jurisdiction}. Keep counsel review before external dispatch.</p>`,
      aiBlocks: matter.matterSpecies === "CONTRACT" ? 1 : 0,
      lockHolder: undefined,
      lockExpiresAt: undefined,
      versions: [
        {
          id: "v1",
          name: "Matter shell import",
          date: new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }),
          content: `<h2>${matter.title}</h2><p>Initial matter-owned drafting shell for ${matter.matterId}.</p>`,
        },
      ],
    };
  };

  const openMatterDraft = (matter: Matter) => {
    setActiveMatterId(matter.matterId);
    setActiveClientId(matter.clientId);
    setActiveDraft(buildMatterDraft(matter));
    writeLogs(`${role} User`, "MATTER_DRAFT_OPEN", `Opened matter-linked structured draft for ${matter.matterId}.`, matter.matterId);
  };

  // ============================================================
  // Sprint 12.3 — Firm-scoped Firestore listeners
  // ------------------------------------------------------------
  // Subscribes clients, workQueue, alerts and auditLogs to their respective
  // firms/{firmId}/<slice> subcollections, mirroring the matters listener
  // contract above. activeDraft binds to a single doc lookup gated on a
  // non-empty draft id (draft-routing lands in Sprint 12.5; until then the
  // listener is inert in production and self-binds to draft-904 in mock mode).
  // ============================================================
  useEffect(() => {
    if (!user || !firmId) {
      setClients(mockOrEmpty(MOCK_CLIENTS_SEED));
      setWorkQueue(mockOrEmpty(MOCK_WORK_QUEUE_SEED));
      setAlerts(mockOrEmpty(MOCK_ALERTS_SEED));
      setAuditLogs(mockOrEmpty(MOCK_AUDIT_LOGS_SEED));
      return;
    }

    const clientsRef = collection(db, "firms", firmId, "clients");
    const unsubClients = onSnapshot(
      clientsRef,
      (snapshot) => {
        if (snapshot.empty) {
          setClients(mockOrEmpty(MOCK_CLIENTS_SEED));
        } else {
          const list: Client[] = [];
          snapshot.forEach((d) => {
            const data = d.data();
            list.push({
              recordId: d.id,
              clientId: typeof data.clientId === "string" && data.clientId ? data.clientId : d.id,
              name: data.name || "",
              status: data.status || "ACTIVE",
              type: data.type || "Entity",
              industry: data.industry || "",
              languages: data.languages || [],
              engagedAt: data.engagedAt || "",
              kycExpiry: data.kycExpiry || "",
              kycStatus: normalizeClientKycStatus(data.kycStatus ?? data.nextKycStatus ?? data.clientKycStatus),
              amlRisk: data.amlRisk || "LOW",
              outstandingAR: typeof data.outstandingAR === "number" ? data.outstandingAR : 0,
              revenueYTD: typeof data.revenueYTD === "number" ? data.revenueYTD : 0,
              contacts: Array.isArray(data.contacts) ? data.contacts : [],
            });
          });
          setClients(list);
        }
      },
      (error) => {
        console.error("Firestore clients snapshot error:", error);
        setClients(mockOrEmpty(MOCK_CLIENTS_SEED));
      },
    );

    const workQueueRef = collection(db, "firms", firmId, "work_queue");
    const unsubWorkQueue = onSnapshot(
      workQueueRef,
      (snapshot) => {
        if (snapshot.empty) {
          setWorkQueue(mockOrEmpty(MOCK_WORK_QUEUE_SEED));
        } else {
          const list: WorkQueueItem[] = [];
          snapshot.forEach((d) => {
            const data = d.data();
            list.push({
              id: d.id,
              title: data.title || "",
              workflow: data.workflow || data.type || "CONTRACT",
              owner: data.owner || data.assigneeName || data.assignedTo || "",
              status: data.status || "OPEN",
              dueOrAge: data.dueOrAge || data.dueAt || data.ageLabel || "",
              progress: typeof data.progress === "number" ? data.progress : typeof data.progressPct === "number" ? data.progressPct : 0,
              reasonBlocked: data.reasonBlocked,
              linkedAlertId: data.linkedAlertId,
              linkedMatterId: data.linkedMatterId,
            });
          });
          setWorkQueue(list);
        }
      },
      (error) => {
        console.error("Firestore work_queue snapshot error:", error);
        setWorkQueue(mockOrEmpty(MOCK_WORK_QUEUE_SEED));
      },
    );

    const alertsRef = collection(db, "firms", firmId, "alerts");
    const unsubAlerts = onSnapshot(
      alertsRef,
      (snapshot) => {
        if (snapshot.empty) {
          setAlerts(mockOrEmpty(MOCK_ALERTS_SEED));
        } else {
          const list: Alert[] = [];
          snapshot.forEach((d) => {
            const data = d.data();
            list.push({
              id: d.id,
              title: data.title || "",
              matterId: data.matterId,
              priority: data.priority || "P2",
              category: data.category || "ALERT",
              status: data.status || "UNRESOLVED",
            });
          });
          setAlerts(list);
        }
      },
      (error) => {
        console.error("Firestore alerts snapshot error:", error);
        setAlerts(mockOrEmpty(MOCK_ALERTS_SEED));
      },
    );

    const auditLogsRef = collection(db, "firms", firmId, "audit_log");
    const unsubAuditLogs = onSnapshot(
      auditLogsRef,
      (snapshot) => {
        if (snapshot.empty) {
          setAuditLogs(mockOrEmpty(MOCK_AUDIT_LOGS_SEED));
        } else {
          const list: AuditLog[] = [];
          snapshot.forEach((d) => {
            const data = d.data();
            list.push({
              id: d.id,
              timestamp: data.timestamp || "",
              actor: data.actor || "",
              role: data.role || "",
              action: data.action || "",
              details: data.details || "",
              matterId: data.matterId,
            });
          });
          // Newest first, mirroring writeLogs() local prepend behaviour.
          list.sort((a, b) => (a.timestamp < b.timestamp ? 1 : -1));
          setAuditLogs(list);
        }
      },
      (error) => {
        console.error("Firestore auditLogs snapshot error:", error);
        setAuditLogs(mockOrEmpty(MOCK_AUDIT_LOGS_SEED));
      },
    );

    return () => {
      unsubClients();
      unsubWorkQueue();
      unsubAlerts();
      unsubAuditLogs();
    };
  }, [user, firmId]);

  // Sprint 12.3 — activeDraft single-doc listener
  // Inert until activeDraft.id is non-empty. In mock mode the seed supplies
  // draft-904 so the editor still wires to a (potentially-missing) draft doc;
  // in production the editor stays on EMPTY_ACTIVE_DRAFT_SEED until route
  // plumbing in Sprint 12.5 calls setActiveDraft({ id, ...stub }) to subscribe.
  useEffect(() => {
    if (!user || !firmId || !activeDraft.id) return;
    const draftRef = doc(db, "firms", firmId, "drafts", activeDraft.id);
    const unsubDraft = onSnapshot(
      draftRef,
      (snap) => {
        if (!snap.exists()) return;
        const data = snap.data() as Partial<ActiveDraftState>;
        setActiveDraft((prev) => ({
          ...prev,
          title: data.title ?? prev.title,
          content: data.content ?? prev.content,
          aiBlocks: typeof data.aiBlocks === "number" ? data.aiBlocks : prev.aiBlocks,
          lockHolder: data.lockHolder ?? prev.lockHolder,
          lockExpiresAt: data.lockExpiresAt ?? prev.lockExpiresAt,
          versions: Array.isArray(data.versions) ? data.versions : prev.versions,
        }));
      },
      (error) => {
        console.error("Firestore activeDraft snapshot error:", error);
      },
    );
    return () => unsubDraft();
    // We intentionally exclude activeDraft fields beyond `id` so the listener
    // does not re-subscribe on every local content edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, firmId, activeDraft.id]);

  const setActiveDraftContent = (newContent: string) => {
    setActiveDraft(prev => ({ ...prev, content: newContent }));
  };

  const claimDraftLock = () => {
    if (activeDraft.lockHolder && activeDraft.lockHolder !== `${role} Mode`) {
      return false; // Active edit lock block!
    }
    const expiry = DateTime.now().plus({ minutes: 5 }).setZone("Asia/Dubai").toFormat("HH:mm:ss");
    setActiveDraft(prev => ({
      ...prev,
      lockHolder: `${role} Mode`,
      lockExpiresAt: expiry
    }));
    writeLogs(`${role} User`, "DRAFT_LOCK_CLAIM", `Claimed exclusive editor lease on draft ${activeDraft.title}. Expires at ${expiry}.`);
    return true;
  };

  const releaseDraftLock = () => {
    setActiveDraft(prev => ({
      ...prev,
      lockHolder: undefined,
      lockExpiresAt: undefined
    }));
    writeLogs(`${role} User`, "DRAFT_LOCK_RELEASE", `Released editor lease on draft ${activeDraft.title}.`);
  };

  const saveDraftVersion = (vName: string) => {
    const newV = {
      id: "v" + (activeDraft.versions.length + 1),
      name: vName,
      date: DateTime.now().setZone("Asia/Dubai").toFormat("dd LLL yyyy, HH:mm"),
      content: activeDraft.content
    };
    setActiveDraft(prev => ({
      ...prev,
      versions: [newV, ...prev.versions]
    }));
    writeLogs(`${role} User`, "DRAFT_VERSION_SAVE", `Saved new named checkpoint: "${vName}".`);
  };

  // ==========================================
  // Interactive Action Simulators §1.5
  // ==========================================

  // Simulates owner dunning sweep on accounts receivable §1.5.1
  const runDunningSweep = () => {
    let affectedClients: string[] = [];
    const updatedClients = clients.map(client => {
      if (client.outstandingAR > 0) {
        affectedClients.push(client.name);
        return { ...client, outstandingAR: Math.max(0, client.outstandingAR - 10000) }; // partially paid MOCK
      }
      return client;
    });
    setClients(updatedClients);
    const summary = `Dunning sweep completed across active firms. Automated recovery letters issued to: ${affectedClients.join(", ")}. Mock recovery of AED 30,000 recorded.`;
    writeLogs(`${role} User`, "FINANCIAL_DUNNING_SWEEP", summary);
    return summary;
  };

  // Rebalances workload drawer action §1.5.2
  const rebalanceWorkload = (fromLawyer: string, toLawyer: string, mId: string) => {
    setMatters(prev => prev.map(matter => {
      if (matter.matterId === mId) {
        const updated = {
          ...matter,
          leadLawyer: toLawyer,
          lastActivity: `Reassigned from ${fromLawyer} to ${toLawyer} via Cockpit Workload Manager.`
        };
        if (firmId) {
          setDoc(doc(db, "firms", firmId, "clients", matter.clientId, "matters", mId), updated, { merge: true }).catch(err => {
            console.error("Error writing updated matter assignment to Firestore:", err);
          });
        }
        return updated;
      }
      return matter;
    }));
    
    // update queue too
    setWorkQueue(prev => prev.map(item => {
      if (item.linkedMatterId === mId) {
        return { ...item, owner: toLawyer };
      }
      return item;
    }));

    writeLogs(`${role} User`, "TEAM_WORKLOAD_REBALANCE", `Successfully reassigned Lead role of ${mId} to ${toLawyer} to prevent burn-out of ${fromLawyer}.`);
  };

  // Case validation action §5.3
  const validateEvidence = (mId: string, evidenceId: string) => {
    setMatters(prev => prev.map(matter => {
      if (matter.matterId === mId) {
        const updatedEvidence = matter.evidence.map(ev => {
          if (ev.id === evidenceId) {
            return {
              ...ev,
              status: "VALIDATED" as const,
              validatedBy: `${role} User`
            };
          }
          return ev;
        });
        
        // Remove Flag from matter if all validated
        const remainingUnvalidated = updatedEvidence.some(v => v.status !== "VALIDATED");
        const updatedFlags = matter.flags.filter(f => f !== "Evidence Unvalidated");
        if (remainingUnvalidated) {
          updatedFlags.push("Evidence Unvalidated");
        }

        const updated = {
          ...matter,
          evidence: updatedEvidence,
          flags: updatedFlags
        };
        if (firmId) {
          setDoc(doc(db, "firms", firmId, "clients", matter.clientId, "matters", mId), updated, { merge: true }).catch(err => {
            console.error("Error writing validated evidence to Firestore:", err);
          });
        }
        return updated;
      }
      return matter;
    }));

    writeLogs(`${role} User`, "EVIDENCE_VALIDATE", `Formally validated security hash and chains-of-custody for file "${evidenceId}" inside Matter ${mId}.`);
  };

  const uploadEvidence = (mId: string, item: Partial<EvidenceItem>) => {
    const newItem: EvidenceItem = {
      id: item.id || "ev-" + Math.floor(Math.random() * 1000),
      name: item.name || "Uploaded_Document.pdf",
      type: item.type || "Expert Report",
      uploadedAt: DateTime.now().setZone("Asia/Dubai").toISO() || new Date().toISOString(),
      status: "NEW",
      size: item.size || "1.2 MB",
      entities: item.entities || ["Global Entities"],
      datesMentioned: item.datesMentioned || ["26 May 2026"],
      admissibilityFlags: item.admissibilityFlags || [],
      excerpt: item.excerpt || "No excerpt parsed. Dynamic AI generation pending analysis.",
      hasAiInsight: true
    };

    setMatters(prev => prev.map(matter => {
      if (matter.matterId === mId) {
        const updated = {
          ...matter,
          evidence: [...matter.evidence, newItem]
        };
        if (firmId) {
          setDoc(doc(db, "firms", firmId, "clients", matter.clientId, "matters", mId), updated, { merge: true }).catch(err => {
            console.error("Error writing uploaded evidence to Firestore:", err);
          });
        }
        return updated;
      }
      return matter;
    }));

    writeLogs(`${role} User`, "EVIDENCE_UPLOAD", `Enqueued new file upload "${newItem.name}" inside Matter ${mId}. 1 AI Credit debited.`);
  };

  const addWitness = (mId: string, witness: Partial<Witness>) => {
    const newWit: Witness = {
      witnessId: "wit-" + Math.floor(Math.random() * 1000),
      name: witness.name || "Amina Al-Mazrouei",
      role: witness.role || "FACT",
      side: witness.side || "NEUTRAL",
      credibility: witness.credibility || "UNASSESSED",
      testimonySummary: witness.testimonySummary || "Testimony parsed via AI. No major contradictions extracted with other statements.",
      contradictionCount: 0,
      crossExamQuestions: [
        "Detail your recollection of direct statements.",
        "Verify standard timeline entries.",
        "Confirm if any contractual deviations were noted in writing."
      ]
    };

    setMatters(prev => prev.map(matter => {
      if (matter.matterId === mId) {
        const updated = {
          ...matter,
          witnesses: [...matter.witnesses, newWit]
        };
        if (firmId) {
          setDoc(doc(db, "firms", firmId, "clients", matter.clientId, "matters", mId), updated, { merge: true }).catch(err => {
            console.error("Error writing new witness to Firestore:", err);
          });
        }
        return updated;
      }
      return matter;
    }));

    writeLogs(`${role} User`, "WITNESS_REGISTER", `Registered case witness "${newWit.name}" (${newWit.role}) under CASE ${mId}.`);
  };

  const addObligation = (mId: string, obligation: Partial<Obligation>) => {
    const newOb: Obligation = {
      id: "ob-" + Math.floor(Math.random() * 1000),
      title: obligation.title || "Custom Obligation",
      dueAt: obligation.dueAt || "2026-06-30",
      status: "PENDING",
      assignedTo: obligation.assignedTo || "Mary K. (MK)",
      triggerType: obligation.triggerType || "Milestone"
    };

    setMatters(prev => prev.map(matter => {
      if (matter.matterId === mId) {
        const updated = {
          ...matter,
          obligations: [...matter.obligations, newOb]
        };
        if (firmId) {
          setDoc(doc(db, "firms", firmId, "clients", matter.clientId, "matters", mId), updated, { merge: true }).catch(err => {
            console.error("Error writing new obligation to Firestore:", err);
          });
        }
        return updated;
      }
      return matter;
    }));

    writeLogs(`${role} User`, "OBLIGATION_ADD", `Added post-execution compliance obligation: "${newOb.title}" scheduled for ${newOb.dueAt}.`, mId);
  };

  const acceptRedlineChange = (mId: string, roundId: number, section: string, accept: boolean) => {
    setMatters(prev => prev.map(matter => {
      if (matter.matterId === mId) {
        const updatedRounds = matter.redlineRounds.map(round => {
          if (round.roundId === roundId) {
            const updatedChanges = round.clauseChanges.map(change => {
              if (change.section === section) {
                return { ...change, status: accept ? "ACCEPTED" as const : "REJECTED" as const };
              }
              return change;
            });
            return { ...round, clauseChanges: updatedChanges };
          }
          return round;
        });

        // Mutate Active Studio Draft if editing
        if (accept && section.includes("Limitation")) {
          setActiveDraftContent(activeDraft.content.replace(
            /strictly capped at 2\x7b1,2\x7d the annual contract fee.*/,
            "completely unlimited for all direct damages, breaches of data protection laws, or indemnities. Acceptable risk registered."
          ));
        }

        const updated = { ...matter, redlineRounds: updatedRounds };
        if (firmId) {
          setDoc(doc(db, "firms", firmId, "clients", matter.clientId, "matters", mId), updated, { merge: true }).catch(err => {
            console.error("Error writing redline decision to Firestore:", err);
          });
        }
        return updated;
      }
      return matter;
    }));

    writeLogs(`${role} User`, "REDLINE_RECONCILE", `Set redline status for "${section}" inside ${mId} is ${accept ? "ACCEPTED" : "REJECTED"}.`);
  };

  // Customizable Reminders §6.1.2
  const addCustomReminder = (title: string, triggerDays: number, eventName: string, channels: string[]) => {
    const dueStr = DateTime.now().plus({ days: triggerDays }).toFormat("yyyy-MM-dd");
    const newEvent: WorkQueueItem = {
      id: "ob-" + Math.floor(Math.random() * 10000),
      title: `Reminder: ${title} (${eventName})`,
      workflow: "ONBOARD",
      owner: `${role} User`,
      status: "OPEN",
      dueOrAge: `${triggerDays} days left (${dueStr})`,
      progress: 0,
      reasonBlocked: `Channels: ${channels.join(", ")}`
    };
    setWorkQueue(prev => [newEvent, ...prev]);
    // AUD-6 (R5): propagate the deadline to the Alerts surface so calendar
    // reminders visibly flow through to the firm-wide alert register.
    const reminderPriority: Alert["priority"] = triggerDays <= 1 ? "P1" : triggerDays <= 3 ? "P2" : "P3";
    const newAlert: Alert = {
      id: "al-rem-" + Math.floor(Math.random() * 100000),
      title: `Reminder: ${title} (${eventName})`,
      matterId: activeMatterId ?? undefined,
      priority: reminderPriority,
      category: "ALERT",
      status: "UNRESOLVED",
    };
    setAlerts(prev => [newAlert, ...prev]);
    writeLogs(`${role} User`, "REMINDER_CREATE", `Registered dynamic relative reminder for "${title}" scheduled ${triggerDays} days prior to "${eventName}".`);
  };

  return (
    <AppContext.Provider
      value={{
        role,
        lang,
        currentRoute,
        activeMatterId,
        activeClientId,
        setRole,
        setLang,
        setCurrentRoute,
        setActiveMatterId,
        setActiveClientId,
        matters,
        clients,
        setClients,
        workQueue,
        setWorkQueue,
        alerts,
        auditLogs,
        rebalanceWorkload,
        runDunningSweep,
        validateEvidence,
        uploadEvidence,
        addWitness,
        addObligation,
        acceptRedlineChange,
        addCustomReminder,
        writeLogs,
        activeDraft,
        openMatterDraft,
        setActiveDraftContent,
        claimDraftLock,
        releaseDraftLock,
        saveDraftVersion,
        aiAttributionOpen,
        setAiAttributionOpen,
        sidebarOpen,
        setSidebarOpen,
        aiCounselOpen,
        setAiCounselOpen,
        aiCounselWidth,
        setAiCounselWidth
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within AppStateProvider");
  }
  return context;
};

// Extracted Specific hooks for Firestore-readiness
export const useClient = (clientId: string) => {
  const { clients } = useAppContext();
  return clients.find(c => c.clientId === clientId || c.recordId === clientId);
};

export const useMatter = (matterId: string) => {
  const { matters } = useAppContext();
  return matters.find(m => m.matterId === matterId);
};

export const useWorkQueue = () => {
  const { workQueue } = useAppContext();
  return workQueue;
};
